import type { RavenStatic } from 'raven-js';

export const ensureFn = (fn: any, fallback = () => {}): Function =>
  typeof fn === 'function' ? fn : fallback;

export function ensurePromise<T extends any>(thing: T): Promise<T> {
  if (thing instanceof Promise) {
    return thing;
  }

  return Promise.resolve(thing);
}

export function filterObject(
  obj: {},
  predicate = ([, v]: [string, any]) => Boolean(v)
) {
  return Object.entries(obj)
    .filter(predicate)
    .reduce(
      (acc, [k, v]) => ({
        /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
        ...acc,
        [k]: v,
      }),
      {}
    );
}

export const pick = <T extends {}, K extends keyof T>(
  source: T = {} as T,
  list: K[] = []
): Pick<T, K> =>
  Object.assign({}, ...list.map((key) => ({ [key]: source[key] })));

export const once = <ReturnType extends any>(
  fn: (...args: any[]) => ReturnType
) => {
  let isCached: boolean;
  let result: any;

  return (...args: any[]): ReturnType => {
    if (!isCached) {
      isCached = true;
      result = fn(...args);
    }
    return result;
  };
};

export function defer(func: Function, args: any[]) {
  return setTimeout(() => {
    func.apply(undefined, args);
  }, 1);
}

export function merge(target: {}, source: {}) {
  const isObject = (obj: any): obj is {} => typeof obj === 'object' && obj;

  if (!isObject(target) || !isObject(source)) {
    return source;
  }

  Object.keys(source).forEach((key) => {
    // @ts-expect-error migrate this properly
    const targetValue = target[key] as any;
    // @ts-expect-error migrate this properly
    const sourceValue = source[key] as any;

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      // @ts-expect-error migrate this properly
      target[key] = targetValue.concat(sourceValue);
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      // @ts-expect-error migrate this properly
      target[key] = merge(Object.assign({}, targetValue), sourceValue);
    } else {
      // @ts-expect-error migrate this properly
      target[key] = sourceValue;
    }
  });

  return target;
}

// Note: params can both be an object or a string
// @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams#parameters
export function createQueryString(params = '') {
  const searchParams = new URLSearchParams(params);

  return [...searchParams.values()].length ? `?${searchParams.toString()}` : '';
}

// Note: params can both be an object or a string
// @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams#parameters
export function createQueryObject(params = '') {
  return Object.fromEntries(new URLSearchParams(params));
}

export const getNavigatorLanguage = (defaultLanguage = 'en-us') => {
  if (typeof navigator === 'object') {
    const preferredLanguage = navigator.languages
      ? navigator.languages[0]
      : navigator.language;

    if (preferredLanguage) {
      return preferredLanguage;
    }
  }

  return defaultLanguage;
};

export const cookieExists = (name: string): boolean => {
  const documentCookie = typeof document === 'object' ? document.cookie : '';

  return documentCookie.split(';').some((i) => i.trim().startsWith(`${name}=`));
};

type HasRaven<K> = K & { Raven: RavenStatic };

export const hasRaven = <K extends Record<string, any>>(
  prop: K
): prop is HasRaven<K> => {
  return (
    typeof prop['Raven'] === 'object' &&
    typeof prop['Raven'].captureException === 'function' &&
    typeof prop['Raven'].captureMessage === 'function'
  );
};
